import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, Grid, CircularProgress,
} from '@mui/material';
import { LockReset as LockResetIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useThemeColors } from '../hooks/useThemeColors';

export default function SetupPassword() {
  const { accentBlue, gradientPrimary } = useThemeColors();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  const [step, setStep] = useState(tokenFromUrl ? 'loading' : 'manual'); // 'loading', 'password', 'manual'
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [token, setToken] = useState(tokenFromUrl || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // If token is in URL, verify it automatically
  useEffect(() => {
    if (tokenFromUrl) {
      verifyToken(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  const verifyToken = async (tokenToVerify) => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/password-reset/verify-token', { token: tokenToVerify });
      setUsername(response.data.username);
      setFullName(response.data.full_name);
      setToken(tokenToVerify);
      setStep('password');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired link');
      setStep('manual');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/password-reset/reset-with-token', {
        token,
        newPassword
      });
      setSuccess('Password set successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (step === 'loading') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: gradientPrimary,
          p: 2
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <CircularProgress sx={{ color: accentBlue, mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Verifying your setup link...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: gradientPrimary,
        p: 2
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <LockResetIcon sx={{ fontSize: 48, color: accentBlue, mb: 1 }} />
          <Typography variant="h5" sx={{ color: accentBlue, fontWeight: 600 }}>
            {step === 'password' ? 'Create Your Password' : 'Set Up Your Password'}
          </Typography>
          {step === 'password' && fullName && (
            <Typography variant="body1" sx={{ mt: 1, color: accentBlue }}>
              Welcome, {fullName}!
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {step === 'password'
              ? 'Create a secure password for your account'
              : 'Enter your username and the setup code from your email, or use the link in your email'
            }
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {step === 'password' ? (
          <form onSubmit={handleSetPassword}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Username"
                  value={username}
                  disabled
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                  helperText="At least 6 characters"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{
                    background: gradientPrimary,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem'
                  }}
                >
                  {loading ? 'Setting Password...' : 'Set Password'}
                </Button>
              </Grid>
            </Grid>
          </form>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please use the link in your welcome email to set up your password. If you don't have the email, contact your administrator.
            </Alert>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Go to Login
            </Button>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="text"
            onClick={() => navigate('/login')}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Already have a password? Login
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
